import type { HandView } from "@numbet/engine";
import { useEffect, useState } from "react";
import { ChipIcon } from "../components/ui.tsx";
import { formatChips, formatValue, isYearUnit } from "../format.ts";
import { t } from "../i18n.ts";

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
const EDGES = ["edge-pink tilt-l", "edge-cyan", "edge-yellow tilt-r"];
const PILLS = ["pill-pink", "pill-cyan", "pill-yellow"];

/** Zählt beim ersten Erscheinen zur Zielzahl hoch – der Moment der Auflösung. */
function CountUp({ value, unit, decimals }: { value: number; unit: string; decimals: number }) {
  const [shown, setShown] = useState(() => (reducedMotion() ? value : 0));
  useEffect(() => {
    if (reducedMotion()) return setShown(value);
    const start = performance.now();
    const duration = 1100;
    let frame = 0;
    const step = (time: number) => {
      const p = Math.min(1, (time - start) / duration);
      const eased = 1 - (1 - p) ** 3;
      const scale = 10 ** decimals;
      setShown(p === 1 ? value : Math.round(value * eased * scale) / scale);
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, decimals]);
  return (
    <>
      {formatValue(shown, unit, decimals)}
      {!isYearUnit(unit) && (
        <>
          {" "}
          <span className="slot-unit">{unit}</span>
        </>
      )}
    </>
  );
}

export function Question({ hand }: { hand: HandView }) {
  const { question } = hand;
  return (
    <div className={`question${question.text.length > 70 ? " long" : hand.phase === "estimate" ? " large" : ""}`}>
      <h1 className="question-text">{question.text}</h1>
      {!isYearUnit(question.unit) && <div className="question-unit">{t.answerIn(question.unit)}</div>}
    </div>
  );
}

export function Board({ hand }: { hand: HandView }) {
  const { question } = hand;
  const [mainPot, ...sidePots] = hand.pots;
  const potTotal = hand.pots.reduce((sum, pot) => sum + pot.amount, 0);
  return (
    <section className="board" aria-label="Spieltisch">
      <div className="board-grid">
        {hand.hints.map((hint, i) => (
          <div key={i} className="hint">
            {hint === null ? (
              <div className="hint-hidden" role="img" aria-label={`${t.hint(i + 1)}: ${t.hiddenHint}`}>
                {i + 1}
              </div>
            ) : (
              <div className={`hint-card hint-shown ${EDGES[i]}`}>
                <span className={`pill ${PILLS[i]} pill-upper`}>{t.hintPill(i + 1)}</span>
                <div className="hint-text">{hint}</div>
              </div>
            )}
          </div>
        ))}
        <div className="board-side">
          {potTotal > 0 && (
          <div className="pot-card">
            <div className="slot-label">{t.pot}</div>
            <div className="slot-value">
              <span key={potTotal} className="bump" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <ChipIcon className="pot-chip" />
                {formatChips(potTotal)}
              </span>
            </div>
            {sidePots.length > 0 && mainPot && (
              <div className="side-pots">
                {[mainPot, ...sidePots].map((pot, i) => (
                  <span key={i}>
                    {i === 0 ? t.pot : t.sidePot(i)}: {formatChips(pot.amount)}
                    {i < sidePots.length ? " · " : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
          )}
          <div className={`answer-card${hand.answer !== null ? " revealed" : ""}`} aria-live="polite">
            <div className="slot-label">{t.answer}</div>
            <div className="slot-value">
              {hand.answer === null ? "????" : <CountUp value={hand.answer} unit={question.unit} decimals={question.decimals} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
